import { KeyRound } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { getApiErrorMessage, post } from '../api';
import { useAuth } from '../context/AuthContext';

export default function Account() {
    const { user } = useAuth();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setError(null);
        setSuccess(false);

        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('New password and confirmation do not match.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await post('/auth/change-password', {
                current_password: currentPassword,
                new_password: newPassword,
            });
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(getApiErrorMessage(payload, 'Could not change your password.'));
            }

            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setSuccess(true);
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Could not change your password.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="content-width mx-auto max-w-xl">
            <div className="surface-card rounded-[28px] p-6 sm:p-7">
                <div className="flex items-center gap-3">
                    <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl"
                        style={{ backgroundColor: 'var(--accent-tint)', color: 'var(--accent)' }}
                    >
                        <KeyRound className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                        <h1 className="text-xl font-semibold">Change password</h1>
                        <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>
                            {user ? `Signed in as ${user.username}. ` : ''}
                            Update the password used to sign in to your workspace.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    <div>
                        <label htmlFor="current-password" className="text-sm font-semibold">
                            Current password
                        </label>
                        <input
                            id="current-password"
                            type="password"
                            value={currentPassword}
                            onChange={(event) => setCurrentPassword(event.target.value)}
                            className="field mt-2 rounded-2xl px-4 py-3 text-sm"
                            autoComplete="current-password"
                            minLength={8}
                            maxLength={128}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="new-password" className="text-sm font-semibold">
                            New password
                        </label>
                        <input
                            id="new-password"
                            type="password"
                            value={newPassword}
                            onChange={(event) => setNewPassword(event.target.value)}
                            className="field mt-2 rounded-2xl px-4 py-3 text-sm"
                            autoComplete="new-password"
                            minLength={8}
                            maxLength={128}
                            required
                        />
                    </div>

                    <div>
                        <label htmlFor="confirm-password" className="text-sm font-semibold">
                            Confirm new password
                        </label>
                        <input
                            id="confirm-password"
                            type="password"
                            value={confirmPassword}
                            onChange={(event) => setConfirmPassword(event.target.value)}
                            className="field mt-2 rounded-2xl px-4 py-3 text-sm"
                            autoComplete="new-password"
                            minLength={8}
                            maxLength={128}
                            required
                        />
                    </div>

                    {error ? (
                        <p className="text-sm" style={{ color: 'var(--danger)' }}>
                            {error}
                        </p>
                    ) : null}
                    {success ? (
                        <p className="text-sm" style={{ color: 'var(--accent)' }}>
                            Password updated.
                        </p>
                    ) : null}

                    <button type="submit" disabled={isSubmitting} className="button-primary w-full rounded-2xl px-4 py-3 text-sm font-semibold">
                        {isSubmitting ? 'Updating...' : 'Update password'}
                    </button>
                </form>
            </div>
        </div>
    );
}
